import { isAxiosError } from 'axios';

import type { RefreshOutcome } from '@/features/auth/tokenRefresher';

import { ENDPOINTS } from '@/constants/api';
import { authTokenStorage } from '@/features/auth/external/authTokenStorage';
import { getApiClient } from '@/hooks/apiClient';

interface TokenRefreshResponse {
  access_token: string;
}

const isTokenRefreshResponse = (v: unknown): v is TokenRefreshResponse =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as Record<string, unknown>).access_token === 'string';

const TERMINAL_REASONS = ['handle_invalid', 'handle_expired', 'grant_revoked'] as const;
type TerminalReason = (typeof TERMINAL_REASONS)[number];

// Classify on the backend's structured `code` ONLY — never on HTTP status or a
// substring. Anything unrecognized (5xx, network, unknown body) defaults to
// `transient` so an ambiguous failure never logs the user out.
function classifyRefreshError(e: unknown): RefreshOutcome {
  if (isAxiosError(e)) {
    const code = (e.response?.data as { code?: string } | undefined)?.code;
    if (code && (TERMINAL_REASONS as readonly string[]).includes(code)) {
      return { status: 'terminal', reason: code as TerminalReason };
    }
  }
  return { status: 'transient' };
}

/**
 * Refresh the companion access token using the opaque session handle. The
 * backend mints a new access token from its server-held refresh token. Returns a
 * RefreshOutcome; never throws across the single-flight boundary.
 *
 * The request carries X-Retry-After-401 + X-Skip-Redirect so the apiClient 401
 * interceptor does NOT re-enter the refresh machinery for the refresh call
 * itself (no recursion).
 */
export async function refreshViaHandle(): Promise<RefreshOutcome> {
  const handle = authTokenStorage.getSessionHandle();
  if (!handle) {
    return { status: 'terminal', reason: 'no_handle' };
  }

  try {
    const client = await getApiClient();
    const { data } = await client.post(ENDPOINTS.companionTokenRefresh, {
      session_handle: handle,
    });
    if (!isTokenRefreshResponse(data)) {
      return { status: 'transient' };
    }
    authTokenStorage.set(data.access_token);
    return { status: 'success', accessToken: data.access_token };
  } catch (e) {
    return classifyRefreshError(e);
  }
}
